import { useRef } from 'react';
import SectionBadge from '../ui/SectionBadge';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Calendar03Icon, SparklesIcon, SecurityCheckIcon, Settings01Icon } from 'hugeicons-react';

const steps = [
  {
    icon: <Calendar03Icon size={32} />,
    title: 'حجز الموعد',
    description: 'ابدأ رحلة التجديد. اختر الباقة المناسبة والوقت المثالي عبر منصتنا الذكية في أقل من دقيقة.',
    color: 'from-primary to-red-600',
    number: '01'
  },
  {
    icon: <SecurityCheckIcon size={32} />,
    title: 'الفحص والاستلام',
    description: 'عند وصولك، يقوم فريقنا بفحص دقيق لسيارتك 360 درجة لتوثيق حالتها وتحديد احتياجاتها بدقة.',
    color: 'from-red-600 to-red-800',
    number: '02'
  },
  {
    icon: <Settings01Icon size={32} />,
    title: 'المعالجة والتتبع المباشر',
    description: 'يبدأ السحر. نستخدم أحدث التقنيات مع إرسال تحديثات فورية وصور لخطوات العمل مباشرة لهاتفك.',
    color: 'from-primary to-rose-600',
    number: '03'
  },
  {
    icon: <SparklesIcon size={32} />,
    title: 'الاستلام والضمان',
    description: 'لحظة الإبهار. استلم سيارتك بحالة "الوكالة" مع شهادة ضمان معتمدة وخدمة متابعة مجانية.',
    color: 'from-red-500 to-primary',
    number: '04'
  }
];

export default function HowItWorks() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const scaleY = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <section ref={containerRef} id="how-it-works" className="py-32 bg-[#050000] relative overflow-hidden" dir="rtl">
      {/* Top Gradient Mask */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#050000] to-transparent z-10 pointer-events-none" />
      {/* Bottom Gradient Mask */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#050000] to-transparent z-10 pointer-events-none" />

      {/* Cinematic Background - Vibrant Red */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-600/30 via-black/40 to-[#050000] pointer-events-none" />
      
      {/* Floating Orbs for "Life" - Increased vibrance */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/40 rounded-full blur-[120px] pointer-events-none mix-blend-screen animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-red-600/30 rounded-full blur-[120px] pointer-events-none mix-blend-screen" />

      <div className="container mx-auto px-4 relative z-20">
        
        {/* Section Header */}
        <div className="text-center mb-24 relative">
          <SectionBadge text="المسار الاحترافي" icon={SparklesIcon} />
          
          <motion.h2 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-5xl md:text-7xl font-black mb-6 text-white tracking-tighter"
          >
            رحلة <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-red-500 drop-shadow-[0_0_20px_rgba(220,10,50,0.5)]">التحول</span>
          </motion.h2>
          <p className="text-gray-300 max-w-xl mx-auto text-lg leading-relaxed font-bold opacity-90">
             من لحظة دخول سيارتك وحتى المغادرة، نضمن لك تجربة احترافية مدروسة بعناية.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Central Progress Line */}
          <div className="absolute right-[28px] md:right-1/2 top-0 bottom-0 w-[2px] bg-white/10 transform md:translate-x-1/2">
             <div className="absolute inset-y-0 -right-4 -left-4 bg-primary/20 blur-xl" />
            <motion.div 
              style={{ scaleY }}
              className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-b from-primary via-red-500 to-primary-dark origin-top shadow-[0_0_20px_rgba(220,20,60,0.6)]"
            />
          </div>

          {/* Steps */}
          <div className="space-y-24 relative z-10 py-10">
            {steps.map((step, index) => (
              <TimelineItem key={index} step={step} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TimelineItem({ step, index }: { step: any, index: number }) {
  const isEven = index % 2 === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={`flex flex-col md:flex-row items-center gap-8 md:gap-20 ${
        isEven ? 'md:flex-row-reverse' : ''
      }`}
    >
      {/* Content Card */}
      <div className="flex-1 w-full pr-16 md:pr-0">
        <div className={`
          relative p-8 rounded-[32px] bg-[#0c0c0c]/80 backdrop-blur-md border border-white/10 hover:border-primary/30 transition-all duration-500 group
          hover:shadow-[0_20px_50px_rgba(220,10,50,0.15)] overflow-hidden
        `}>
          {/* Vibrant Glow Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-[0.05] transition-opacity duration-500`} />
          
          <div className="relative z-10 flex flex-col md:flex-row gap-6 items-start md:items-center">
             <div className={`
                w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} p-[1.5px]
                transform group-hover:scale-110 transition-transform duration-500 shadow-xl
             `}>
               <div className="w-full h-full bg-[#080808] rounded-2xl flex items-center justify-center text-white relative overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-30`} />
                  <div className="relative z-10 shrink-0">{step.icon}</div>
               </div>
             </div>
             
             <div className="text-right flex-1">
                <h3 className="text-2xl font-black mb-2 text-white group-hover:text-primary transition-colors tracking-tight">
                  {step.title}
                </h3>
                <p className="text-gray-200 text-sm leading-relaxed font-bold opacity-100">
                  {step.description}
                </p>
             </div>
          </div>

          {/* Large Number Background */}
          <div className={`absolute -bottom-6 ${isEven ? '-right-6' : '-left-6'} text-9xl font-black text-white/[0.06] select-none pointer-events-none italic tracking-tighter`}>
            {step.number}
          </div>
        </div>
      </div>

      {/* Center Checkpoint */}
      <div className="absolute right-[14px] md:right-1/2 top-0 md:top-auto w-8 h-8 flex items-center justify-center transform md:translate-x-1/2 z-20">
         <div className="w-8 h-8 rounded-full bg-[#080808] border border-white/20 flex items-center justify-center relative shadow-2xl">
            <div className={`w-3.5 h-3.5 rounded-full bg-gradient-to-tr ${step.color} shadow-[0_0_15px_rgba(220,10,50,0.6)]`} />
            {/* Ping Effect */}
            <div className={`absolute inset-0 rounded-full bg-gradient-to-tr ${step.color} opacity-40 animate-ping`} />
         </div>
      </div>

      {/* Spacer */}
      <div className="flex-1 hidden md:block" />
    </motion.div>
  );
}
